import z from 'zod'
import {
  type CalendarEventData,
  type CompanyId,
  type EventType,
  getName,
  isCloseEvent,
  type TeamData,
  type TeamId,
  isEmpty,
  type CalendarEventDataWithDetails,
  isIncludedIn,
  isTeamId,
  type EventOutcomeData,
  isEventOutcomeId,
} from '@terros/common'
import { type ProfileState, useProfile } from '../user'
import { type SelectItem } from '../types'
import { getPermissionFromMap } from '../permissions'

export const FILTERABLE_EVENT_TYPES: SelectItem<EventType>[] = [
  {
    value: 'Consultation',
    label: 'Close Event',
  },
  {
    value: 'Comeback',
    label: 'Personal Comeback',
  },
]

export const AssignmentStateSchema = z.enum(['All', 'Unassigned', 'Assigned'])
export type AssignmentState = z.infer<typeof AssignmentStateSchema>

export const FILTERABLE_ASSIGNMENT_STATES: SelectItem<AssignmentState>[] = [
  {
    value: 'All',
    label: 'Show All',
  },
  {
    value: 'Unassigned',
    label: 'Show Only Unassigned',
  },
  {
    value: 'Assigned',
    label: 'Show Only Assigned',
  },
]

export function toOutcomeItems(outcomes: EventOutcomeData[] | undefined): SelectItem[] {
  if (!outcomes) return []
  return outcomes.map((outcome) => ({ label: outcome.name, value: outcome.outcomeId }))
}

export function toOutcomeFilterValue(selected: string[]): string[] {
  return selected.filter(isEventOutcomeId)
}

export function toOutcomeFilter(outcomes: EventOutcomeData[] | undefined, statuses: string[]): string[] {
  if (!outcomes) return []
  const selectedOutcomes = outcomes.filter((o) => statuses.includes(o.outcomeId))
  // Using both id and name for backward compatibility with old events
  return selectedOutcomes.flatMap((o) => [o.outcomeId, o.name])
}

export type CalendarViewAsSelectItem = SelectItem<CompanyId | TeamId | 'user'>

export function toViewAsDropdownItems(
  profile: ProfileState,
  teams: TeamData[] | undefined
): CalendarViewAsSelectItem[] {
  const { user, company } = profile
  const permissionMap = getPermissionFromMap(profile, 'event:read', teams)

  if (!company) return []
  if (!user) return []
  const items: CalendarViewAsSelectItem[] = [
    {
      label: getName(user),
      value: 'user',
    },
  ]

  if (permissionMap.includes(company.companyId)) {
    items.push({
      label: company.name,
      value: company.companyId,
    })
  }

  items.push(...toCalendarViewAsTeamOptions(profile, teams))
  return items
}

export const toCalendarViewAsTeamOptions = (
  profile: ProfileState,
  teams: TeamData[] | undefined
): SelectItem<TeamId>[] => {
  const { company } = profile
  const permissionMap = getPermissionFromMap(profile, 'event:read', teams)
  const teamIds = permissionMap.filter(isTeamId)
  return (
    teams
      ?.filter((team) => teamIds.includes(team.teamId) || (team.parentId && teamIds.includes(team.parentId)))
      .map((team) => teamDataToItem(team, company?.levelNames?.[team.level])) ?? []
  )
}

const teamDataToItem = (team: TeamData, levelName?: string): SelectItem<TeamId> => ({
  label: getTeamLabel(team, levelName),
  value: team.teamId,
})

const getTeamLabel = (team: TeamData, levelName?: string): string => {
  if (levelName) return `${levelName}: ${team.name}`
  return team.name
}

export function useCanEditEventOutcome(event: CalendarEventData): boolean {
  const { user, hasPermission } = useProfile()
  const userId = user?.userId

  // only close events can have outcomes
  if (!isCloseEvent(event)) return false

  // closers can always edit outcomes
  if (event.attendeeId === userId) return true

  // setters can edit outcomes
  if (event.ownerId === userId) {
    // but only if the event hasn't begun
    if (Date.now() < event.eventDate) return true
  }

  // and dispatchers can always edit outcomes
  return hasPermission('event:manage')
}

export function eventMatchesStringFilter(event: CalendarEventDataWithDetails, filter: string): boolean {
  if (isEmpty(filter)) return true
  const lower = filter.toLowerCase()
  const compareList = [
    event.title,
    event.location?.oneLine,
    event.notes,
    event.resident?.phone,
    stripNonNumbers(event.resident?.phone),
    event.resident?.email,
  ]
  return compareList.some((c) => isIncludedIn(c, lower))
}

const nonNumberRegex = /[^0-9]/g

function stripNonNumbers(text?: string): string | undefined {
  if (text === undefined) return
  return text.replace(nonNumberRegex, '')
}

export function shouldEventRequireAccount(isWorkflowEnabled: boolean, eventType: EventType): boolean {
  if (!isWorkflowEnabled) return false
  return shouldEventRequireLocation(eventType)
}

export function shouldEventRequireLocation(eventType: EventType): boolean {
  return eventType === 'Consultation' || eventType === 'Comeback'
}
